import React, { useState, useContext, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import { SketchPicker } from 'react-color';
import { AuthContext } from '../context/AuthContext';
import NationSelector from '../components/NationSelector';
import { ErrorContext } from '../context/ErrorContext';
import './css/AddAllianceModal.css';

const apiUrl = API_URL;
const normalizeEntityId = (value) => String(value?._id || value || '').trim();

// Ensure this line is present to bind modal to app root for accessibility (important for React Modal)
Modal.setAppElement('#root');

const AddAllianceModal = ({ isOpen, onRequestClose, onAllianceAdded }) => {
  const { showError } = useContext(ErrorContext);
  const { user } = useContext(AuthContext);
  const axios = axiosInstance;
  const [nations, setNations] = useState([]);
  const [selectedNation, setSelectedNation] = useState(user.nation);
  const [name, setName] = useState('');
  const [acronym, setAcronym] = useState('');
  const [territoryColor, setTerritoryColor] = useState('0,0,0');
  const [farmAlliance, setFarmAlliance] = useState(false);

  const userNationId = normalizeEntityId(user?.nation);
  const isNationAdmin = String(user?.role || '') === 'nationAdmin';

  const fetchSupportingData = useCallback(async () => {
    const allNations = await axios.get(`${apiUrl}/nations`);
    setNations(allNations.data);
  }, [axios]);

  useEffect(() => {
    fetchSupportingData();
  }, [fetchSupportingData]);

  useEffect(() => {
    if (!isOpen) return;
    if (isNationAdmin) {
      const ownNation = (nations || []).find((nation) => String(nation?._id || '') === userNationId);
      setSelectedNation(ownNation || userNationId || user?.nation || '');
      return;
    }
    if (!selectedNation && user?.nation) {
      setSelectedNation(user.nation);
    }
  }, [isNationAdmin, isOpen, nations, selectedNation, user?.nation, userNationId]);

  const handleAddAlliance = async () => {
    try {
      let nationId = '';
      if (isNationAdmin) {
        nationId = userNationId;
      } else if (selectedNation?._id) {
        nationId = selectedNation._id;
      } else {
        nationId = normalizeEntityId(selectedNation);
      }

      if (!nationId) {
        showError('Please select a nation before adding an alliance.');
        return;
      }

      const newAlliance = { name, acronym, territoryColor, farmAlliance };
      await axios.post(`${apiUrl}/nations/${nationId}/alliances`, newAlliance);
      onRequestClose();
      onAllianceAdded();
    } catch (error) {
      const errorField = error?.response?.data?.field || error?.field;
      if (errorField === 'name') {
        setName('');
      } else if (errorField === 'acronym') {
        setAcronym('');
      }
      showError(error?.response?.data?.message || error?.message || 'Failed to add alliance.');
    }
  };

  const handleColorChangeComplete = (color) => {
    setTerritoryColor(`${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="add-alliance-modal"          // Modal content class
      overlayClassName="add-alliance-overlay" // Overlay class
      contentLabel="Add Alliance"
    >
      <h2 className="modal-title">Add Alliance</h2>

      {(String(user?.role || '') === 'superUser' || String(user?.role || '') === 'supporter') ? (
        <NationSelector
          nations={nations}
          selectedNation={selectedNation}
          setSelectedNation={setSelectedNation}
          className="modal-input"
        />
      ) : (
        <div className="modal-input">
          <label>
            Nation: {selectedNation?.number || userNationId || ''}
          </label>
        </div>
      )}

      <input
        type="text"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="modal-input"
      />

      <input
        type="text"
        placeholder="Acronym"
        value={acronym}
        onChange={(e) => setAcronym(e.target.value)}
        className="modal-input"
      />

      <div className="color-picker">
        <label>Territory Color</label>
        <SketchPicker
          color={"rgb(" + territoryColor + ")"}
          onChangeComplete={handleColorChangeComplete}
        />
      </div>

      <label className="modal-checkbox">
        <input
          type="checkbox"
          checked={farmAlliance}
          onChange={(event) => setFarmAlliance(event.target.checked)}
        />
        Farm alliance
      </label>

      <div className="modal-buttons">
        <button onClick={handleAddAlliance} className="modal-button">Add Alliance</button>
        <button onClick={onRequestClose} className="modal-button close-button">Close</button>
      </div>
    </Modal>
  );
};

export default AddAllianceModal;
